<template>
    <div class="paper-detail-page animation-fadeIn" v-loading="loading">
        <div class="detail-container" v-if="paper">
            <!-- Left Main Content -->
            <div class="main-content">
                <div class="paper-header-card">
                    <div class="breadcrumb">
                        <el-breadcrumb separator="/">
                            <el-breadcrumb-item :to="{ path: '/papers' }">试卷列表</el-breadcrumb-item>
                            <el-breadcrumb-item>{{ paper.name }}</el-breadcrumb-item>
                        </el-breadcrumb>
                    </div>

                    <div class="paper-title-row">
                        <h1 class="paper-title">{{ paper.name }}</h1>
                        <div class="paper-actions">
                            <el-button v-if="paper.fileUrl || paper.paperQuestions?.length" type="warning" size="large"
                                @click="initiateDownload">
                                <el-icon>
                                    <Download />
                                </el-icon> 下载试卷
                            </el-button>
                            <!-- PPT 导出功能暂未上线 -->
                            <el-button v-if="false" type="warning" size="large" @click="downloadPpt"
                                :loading="exportLoading" style="background-color: #ff9900; border-color: #ff9900;">
                                <el-icon>
                                    <Document />
                                </el-icon> 导出 PPT 课件
                            </el-button>
                            <el-button type="primary" size="large" @click="usePaper">
                                <el-icon>
                                    <DocumentCopy />
                                </el-icon> 使用此卷
                            </el-button>
                        </div>
                    </div>

                    <div class="paper-meta-info">
                        <el-tag :type="getCategoryType(paper.category)" effect="dark">{{ paper.category }}</el-tag>
                        <span class="meta-item"><el-icon>
                                <Calendar />
                            </el-icon> {{ paper.year }}</span>
                        <span class="meta-item"><el-icon>
                                <Location />
                            </el-icon> {{ paper.region }} {{ paper.city }}</span>
                        <span class="meta-item"><el-icon>
                                <User />
                            </el-icon> {{ paper.grade }}</span>
                        <span class="meta-item"><el-icon>
                                <Download />
                            </el-icon> {{ paper.downloadCount || 0 }} 次下载</span>
                        <span class="meta-item"><el-icon>
                                <View />
                            </el-icon> {{ paper.viewCount || 0 }} 次浏览</span>
                        <span class="meta-item" v-if="displayQuestionCount"><el-icon>
                                <List />
                            </el-icon> 共 {{ displayQuestionCount }} 题</span>
                    </div>

                    <div class="paper-mode-inline">
                        <div class="mode-inline-head">
                            <el-tag :type="previewModeInfo.tagType" effect="plain" round>{{ previewModeInfo.tag }}</el-tag>
                            <span class="mode-ext" v-if="paper.fileType">{{ paper.fileType.toUpperCase() }}</span>
                        </div>
                        <p class="mode-inline-desc">{{ previewModeInfo.description }}</p>
                    </div>
                </div>

                <!-- Preview Section -->
                <div class="content-section" v-if="paper.previewImages?.length">
                    <div class="section-title">
                        <el-icon>
                            <View />
                        </el-icon> {{ imagePreviewTitle }}
                    </div>
                    <div class="paper-gallery">
                        <div v-for="(img, idx) in paper.previewImages" :key="idx" class="page-wrapper"
                            :class="{ 'page-wrapper--locked': isImageLocked(idx) }">
                            <el-image :src="img" lazy :preview-src-list="allowedPreviewImages"
                                :initial-index="isImageLocked(idx) ? 0 : idx" class="page-img" fit="contain"
                                :preview-teleported="true" />
                            <div v-if="isImageLocked(idx)" class="page-lock-overlay" @click="initiateDownload">
                                <el-icon class="lock-icon">
                                    <Lock />
                                </el-icon>
                                <span class="lock-text">付费后解锁完整卷面</span>
                            </div>
                            <div class="page-num">第 {{ idx + 1 }} 页 / 共 {{ paper.previewImages.length }} 页</div>
                        </div>
                    </div>
                </div>

                <!-- Web Preview Section (PDF/Office) -->
                <div class="content-section" v-else-if="paper.fileUrl && canPreview">
                    <div class="section-title">
                        <el-icon>
                            <Document />
                        </el-icon> 在线预览
                    </div>
                    <div class="iframe-wrapper">
                        <iframe :src="previewUrl" width="100%" height="800px" frameborder="0"></iframe>
                    </div>
                </div>

                <!-- Archive Preview Section -->
                <div class="content-section archive-section" v-if="paper.fileUrl && isArchiveType">
                    <div class="section-title">
                        <el-icon>
                            <FolderOpened />
                        </el-icon> 压缩包预览
                    </div>
                    <div class="archive-preview-panel">
                        <div class="archive-icon">
                            <el-icon>
                                <FolderOpened />
                            </el-icon>
                        </div>
                        <div class="archive-content">
                            <h3 class="archive-title">当前试卷为压缩包上传（{{ paper.fileType?.toUpperCase() }}）</h3>
                            <p class="archive-desc">平台暂不支持在线解压预览，请下载后在本地查看完整内容。</p>
                            <div class="archive-tags">
                                <el-tag effect="plain" type="warning">{{ paper.fileType?.toUpperCase() }}</el-tag>
                                <el-tag effect="plain" type="info">
                                    {{ paper.previewImages?.length ? '含卷面截图预览' : '无卷面截图' }}
                                </el-tag>
                                <el-tag v-if="paper.paperQuestions?.length" effect="plain" type="success">含结构化题目
                                </el-tag>
                            </div>
                        </div>
                        <div class="archive-actions">
                            <el-button type="warning" size="large" @click="initiateDownload">
                                <el-icon>
                                    <Download />
                                </el-icon> 下载压缩包
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- Questions List -->
                <div class="content-section exam-paper-section" v-if="paper.paperQuestions?.length">
                    <div class="exam-paper-head">
                        <h2 class="exam-paper-title">{{ paper.name }}</h2>
                        <div class="exam-paper-meta-line">
                            <span>{{ paper.region || '全国' }}</span>
                            <span>{{ paper.year || '--' }}</span>
                            <span>{{ paper.grade || '--' }}</span>
                            <span>共 {{ paper.paperQuestions.length }} 题</span>
                        </div>
                    </div>

                    <div class="section-header exam-toolbar">
                        <div class="section-title exam-section-title">
                            <el-icon>
                                <List />
                            </el-icon> 试卷题目预览（按题型分组）
                        </div>
                        <el-button v-if="hasPaid || isFree" type="primary" link @click="addAllToBasket">+
                            全部加入试题篮</el-button>
                    </div>

                    <div v-for="group in filteredGroupedQuestions" :key="group.displayName" class="q-group">
                        <div class="g-title">{{ group.displayName }}</div>
                        <template v-for="(pq, index) in group.questions" :key="pq.id">

                            <!-- Single paywall wall – only before the first locked question in the whole paper -->
                            <div v-if="isFirstLockedQuestion(pq)" class="paywall-wall">
                                <div class="paywall-wall-inner">
                                    <div class="paywall-wall-icon">
                                        <el-icon>
                                            <Lock />
                                        </el-icon>
                                    </div>
                                    <h3 class="paywall-wall-title">解锁完整试卷</h3>
                                    <p class="paywall-wall-desc">
                                        已为您免费展示前 {{ previewLimit }} 道题 · 剩余
                                        <strong>{{ paper.paperQuestions.length - previewLimit }}</strong>
                                        道题付费后可查看，并支持下载 Word 版试卷
                                    </p>
                                    <div class="paywall-wall-price" v-if="paperPrice > 0">
                                        <span class="price-unit">¥</span>
                                        <span class="price-val">{{ (paperPrice / 100).toFixed(2) }}</span>
                                    </div>
                                    <button class="paywall-premium-btn" @click="initiateDownload">
                                        <el-icon class="btn-lock">
                                            <Lock />
                                        </el-icon>
                                        立即解锁全部内容
                                    </button>
                                </div>
                            </div>

                            <!-- Question item -->
                            <div :class="['q-item', { 'q-item--locked': isQuestionLocked(pq) }]">
                                <!-- Minimal lock watermark for locked questions (no CTA text) -->
                                <div v-if="isQuestionLocked(pq)" class="lock-watermark" @click="initiateDownload">
                                    <el-icon class="lock-watermark-icon">
                                        <Lock />
                                    </el-icon>
                                </div>

                                <div class="q-body" :class="{ 'q-body--blurred': isQuestionLocked(pq) }">
                                    <div class="q-text">
                                        <span class="q-no">{{ getGlobalQuestionIndex(pq) + 1 }}.</span>
                                        <div class="q-content">
                                        <MdEditor preview-only preview-theme="github"
                                            :model-value="pq.question?.content || ''" />
                                        </div>
                                    </div>
                                    <div class="q-opts" v-if="pq.question?.options?.length"
                                        :class="getLayoutClass(pq.question)">
                                         <div v-for="opt in pq.question.options" :key="opt.id" class="opt-item">
                                            <span class="opt-label">{{ opt.label }}.</span>
                                            <MdEditor preview-only preview-theme="github"
                                                :model-value="opt.content || ''" />
                                        </div>
                                    </div>
                                </div>
                                <div class="q-actions" v-if="!isQuestionLocked(pq)">
                                    <el-button :type="isInBasket(pq.question) ? 'danger' : 'primary'" link
                                        @click="toggleBasket(pq.question)">
                                        {{ isInBasket(pq.question) ? '移出试题篮' : '加入试题篮' }}
                                    </el-button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- Paywall wall – bottom (shown again after all locked questions) -->
                    <div v-if="!hasPaid && !isFree && previewLimit > 0 && paper.paperQuestions.length > previewLimit"
                        class="paywall-wall paywall-wall--bottom">
                        <div class="paywall-wall-inner">
                            <div class="paywall-wall-icon">
                                <el-icon>
                                    <Lock />
                                </el-icon>
                            </div>
                            <h3 class="paywall-wall-title">解锁完整试卷</h3>
                            <p class="paywall-wall-desc">
                                已为您免费展示前 {{ previewLimit }} 道题 · 剩余
                                <strong>{{ paper.paperQuestions.length - previewLimit }}</strong>
                                道题付费后可查看，并支持下载 Word 版试卷
                            </p>
                            <div class="paywall-wall-price" v-if="paperPrice > 0">
                                <span class="price-unit">¥</span>
                                <span class="price-val">{{ (paperPrice / 100).toFixed(2) }}</span>
                            </div>
                            <button class="paywall-premium-btn" @click="initiateDownload">
                                <el-icon class="btn-lock">
                                    <Lock />
                                </el-icon>
                                立即解锁全部内容
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Sidebar -->
            <div class="sidebar">
                <!-- Similar Papers -->
                <div class="sidebar-card">
                    <div class="card-title">相似试卷</div>
                    <div class="sidebar-list">
                        <div v-for="p in similarPapers" :key="p.id" class="sidebar-item" @click="goToPaper(p)">
                            <div class="item-name" :title="p.name">{{ p.name }}</div>
                            <div class="item-meta">
                                <span>{{ p.year }}</span>
                                <span>{{ p.region }}</span>
                            </div>
                        </div>
                        <el-empty v-if="similarPapers.length === 0" description="暂无推荐" :image-size="60" />
                    </div>
                </div>

                <!-- Related Albums -->
                <div class="sidebar-card">
                    <div class="card-title">相关专辑/题单</div>
                    <div class="sidebar-list">
                        <div v-for="list in relevantLists" :key="list.id" class="sidebar-item" @click="goToList(list)">
                            <div class="item-name">{{ list.title }}</div>
                            <div class="item-meta">
                                <span>{{ list.questions?.length || 0 }} 题</span>
                                <span>{{ list.isPublic ? '公共' : '私有' }}</span>
                            </div>
                        </div>
                        <el-empty v-if="relevantLists.length === 0" description="暂无相关专辑" :image-size="60" />
                    </div>
                </div>

                <div class="sticky-actions">
                    <el-button type="primary" class="full-btn" size="large" @click="usePaper">
                        智能组卷
                    </el-button>
                </div>
            </div>
        </div>
        <el-empty v-else-if="!loading" description="未找到该试卷" />

        <!-- Download Payment Dialog -->
        <DownloadPayDialog v-model="payDialogVisible" :paperId="paper?.id" :paperName="paper?.name"
            @success="handleDownloadSuccess" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
    Download, DocumentCopy, Calendar, Location, User, View, List, Document, Lock, FolderOpened
} from '@element-plus/icons-vue'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import api, { getUser, getToken } from '@/utils/api'
import { downloadFile } from '@/utils/download'
import DownloadPayDialog from '../components/DownloadPayDialog.vue'

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const paper = ref(null)
const similarPapers = ref([])
const relevantLists = ref([])
const basket = ref([])
const exportLoading = ref(false)
const payDialogVisible = ref(false)

// Paywall state
const hasPaid = ref(false)
const isFree = ref(true)
const previewLimit = ref(0)
const paperPrice = ref(0)

const paperId = computed(() => route.params.id)
const currentUserId = computed(() => getUser()?.id)

const fetchPaperDetail = async () => {
    if (!paperId.value) return
    loading.value = true
    try {
        const res = await api.get(`/api/papers/${paperId.value}`)
        paper.value = res.data
        fetchRecommendations()
        recordPaperView()
        await checkPaperAccess()
    } catch (e) {
        ElMessage.error('加载试卷详情失败')
    } finally {
        loading.value = false
    }
}

// 记录试卷浏览量（失败不影响页面展示）
const recordPaperView = async () => {
    try {
        const res = await api.post(`/api/papers/${paperId.value}/view`)
        if (paper.value && typeof res.data?.viewCount === 'number') {
            paper.value.viewCount = res.data.viewCount
        }
    } catch (e) {
        // 静默失败：浏览量统计不应阻塞详情页
    }
}

const checkPaperAccess = async () => {
    try {
        const res = await api.get(`/api/papers/${paperId.value}/access`)
        hasPaid.value = res.data.has_access
        isFree.value = res.data.is_free
        previewLimit.value = res.data.preview_limit || 0
        paperPrice.value = res.data.price || 0
    } catch (e) {
        // If the check fails, default to allowing free preview
        isFree.value = true
    }
}

// Returns the global index across all groups for question lock checks
const getGlobalQuestionIndex = (pq) => {
    const allSorted = paper.value?.paperQuestions
        ? [...paper.value.paperQuestions].sort((a, b) => a.order - b.order)
        : []
    return allSorted.findIndex(q => q.id === pq.id)
}

const isQuestionLocked = (pq) => {
    if (hasPaid.value || isFree.value || previewLimit.value === 0) return false
    return getGlobalQuestionIndex(pq) >= previewLimit.value
}

// Returns true only for the FIRST locked question (so the paywall wall renders once)
const isFirstLockedQuestion = (pq) => {
    if (hasPaid.value || isFree.value || previewLimit.value === 0) return false
    return getGlobalQuestionIndex(pq) === previewLimit.value
}

const imagePreviewLimit = ref(1) // Only show the first page for free

const isImageLocked = (idx) => {
    if (hasPaid.value || isFree.value) return false
    return idx >= imagePreviewLimit.value
}

const allowedPreviewImages = computed(() => {
    if (!paper.value?.previewImages) return []
    if (hasPaid.value || isFree.value) return paper.value.previewImages
    return paper.value.previewImages.slice(0, imagePreviewLimit.value)
})

const fetchRecommendations = async () => {
    try {
        // 1. Fetch Similar Papers
        const name = paper.value.name || ''
        let keyword = name.replace(/[0-9]/g, '').replace(/[第周 \-]/g, '')
        if (keyword.length > 4) keyword = keyword.substring(0, 4)
        if (!keyword && name.length > 2) keyword = name.substring(0, 2)

        let paperResults = []

        // Stage 1: Keyword + Category + Grade
        const res1 = await api.get('/api/papers', {
            params: { keyword: keyword || undefined, category: paper.value.category, grade: paper.value.grade, pageSize: 10 }
        })
        paperResults = res1.data.papers || []

        // Stage 2 fallback: Category + Grade
        if (paperResults.length < 5) {
            const res2 = await api.get('/api/papers', {
                params: { category: paper.value.category, grade: paper.value.grade, pageSize: 10 }
            })
            const add2 = (res2.data.papers || []).filter(p => !paperResults.some(r => r.id === p.id))
            paperResults = [...paperResults, ...add2]
        }

        // Stage 3 fallback: Just Grade
        if (paperResults.length < 5) {
            const res3 = await api.get('/api/papers', {
                params: { grade: paper.value.grade, pageSize: 10 }
            })
            const add3 = (res3.data.papers || []).filter(p => !paperResults.some(r => r.id === p.id))
            paperResults = [...paperResults, ...add3]
        }

        // Stage 4 ultimate fallback: Hot Papers
        if (paperResults.length < 5) {
            const hotRes = await api.get('/api/papers/hot')
            const hots = (hotRes.data.papers || []).filter(p => !paperResults.some(r => r.id === p.id))
            paperResults = [...paperResults, ...hots]
        }

        similarPapers.value = paperResults
            .filter(p => p.id !== parseInt(paperId.value))
            .slice(0, 5)

        // 2. Fetch Relevant Albums / Lists
        let listResults = []

        // Stage 1: By grade if available (Get all public lists first)
        const listsRes = await api.get('/api/question-lists')
        const allLists = listsRes.data.lists || []

        // Try to find lists that might match (optional metadata match if we had it, for now just slice)
        listResults = allLists.slice(0, 5)

        // Fallback: Use Hot Lists if needed or to fill up
        if (listResults.length < 5) {
            try {
                const hotListsRes = await api.get('/api/question-lists/hot')
                const hotLists = (hotListsRes.data.lists || []).filter(l => !listResults.some(r => r.id === l.id))
                listResults = [...listResults, ...hotLists]
            } catch (e) {
                console.warn('Failed to fetch hot lists', e)
            }
        }

        relevantLists.value = listResults.slice(0, 5)
    } catch (e) {
        console.error('Failed to fetch recommendations', e)
    }
}

const loadBasket = async () => {
    if (!currentUserId.value) return
    try {
        const res = await api.get(`/api/basket?userId=${currentUserId.value}`)
        basket.value = res.data || []
    } catch (e) { }
}

const getCategoryType = (cat) => {
    if (cat?.includes('真题')) return 'danger'
    if (cat?.includes('模拟')) return 'warning'
    if (cat?.includes('期中') || cat?.includes('期末')) return 'primary'
    return 'info'
}

const canPreview = computed(() => {
    const type = paper.value?.fileType?.toLowerCase()
    return ['pdf', 'docx', 'doc'].includes(type)
})

const isArchiveType = computed(() => {
    const type = paper.value?.fileType?.toLowerCase()
    return ['zip', 'rar', '7z'].includes(type)
})

// Prefer the real parsed question count; fall back to the declared count for
// raw-file uploads that have no structured questions yet.
const displayQuestionCount = computed(() => paper.value?.paperQuestions?.length || paper.value?.questionCount || 0)

const previewModeInfo = computed(() => {
    if (isArchiveType.value) {
        return {
            key: 'archive',
            tag: '压缩包上传',
            tagType: 'warning',
            title: '压缩包试卷预览模式',
            description: '支持展示基础信息、卷面截图（若有）与下载入口，适合整卷资料包分发。'
        }
    }
    if ((paper.value?.paperQuestions?.length || 0) > 0) {
        return {
            key: 'parsed',
            tag: '解析试卷',
            tagType: 'success',
            title: '结构化题目预览模式',
            description: '支持按题型分组浏览、题目筛读与加入试题篮，适合按题精细使用。'
        }
    }
    if (paper.value?.fileUrl) {
        return {
            key: 'document',
            tag: '文件上传',
            tagType: 'primary',
            title: '文档在线预览模式',
            description: '支持 Word/PDF 在线浏览，保留原始排版，适合整卷快速查阅。'
        }
    }
    return {
        key: 'default',
        tag: '标准模式',
        tagType: 'info',
        title: '试卷预览模式',
        description: '当前试卷展示基础信息，待补充可预览内容。'
    }
})

const imagePreviewTitle = computed(() => {
    if (previewModeInfo.value.key === 'archive') return '压缩包附图预览'
    if (previewModeInfo.value.key === 'parsed') return '解析卷面预览'
    return '试卷原卷预览'
})

const previewUrl = computed(() => {
    if (!paper.value?.fileUrl) return ''
    const type = paper.value.fileType?.toLowerCase()
    if (type === 'pdf') return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(paper.value.fileUrl)}`
    if (['docx', 'doc'].includes(type)) return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(paper.value.fileUrl)}`
    return paper.value.fileUrl
})

const filteredGroupedQuestions = computed(() => {
    if (!paper.value?.paperQuestions) return []
    const sorted = [...paper.value.paperQuestions].sort((a, b) => a.order - b.order)
    const groups = []
    let currentGroup = null
    let cnIdx = 0
    const cnNums = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十']

    sorted.forEach(pq => {
        const typeName = { 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }[pq.question?.type] || '其他'
        if (!currentGroup || currentGroup.typeName !== typeName) {
            currentGroup = {
                displayName: `${cnNums[cnIdx++]}、${typeName}`,
                typeName: typeName,
                questions: []
            }
            groups.push(currentGroup)
        }
        currentGroup.questions.push(pq)
    })
    return groups
})

const isInBasket = (q) => q && basket.value.some(bq => bq.id === q.id)

const toggleBasket = async (q) => {
    if (!currentUserId.value) return ElMessage.warning('请先登录')
    const inB = isInBasket(q)
    try {
        await api.post(`/api/basket/${inB ? 'remove' : 'add'}`, { userId: currentUserId.value, questionId: q.id })
        if (inB) basket.value = basket.value.filter(bq => bq.id !== q.id)
        else basket.value.push(q)
        ElMessage.success(inB ? '已移除' : '已入篮')
    } catch (e) { ElMessage.error('操作失败') }
}

const addAllToBasket = async () => {
    if (!currentUserId.value) return ElMessage.warning('请先登录')
    if (!paper.value?.paperQuestions) return
    let count = 0
    for (const pq of paper.value.paperQuestions) {
        if (pq.question && !isInBasket(pq.question)) {
            await api.post('/api/basket/add', { userId: currentUserId.value, questionId: pq.question.id })
            basket.value.push(pq.question)
            count++
        }
    }
    ElMessage.success(`成功添加 ${count} 题`)
}

const initiateDownload = () => {
    if (paper.value?.id) {
        payDialogVisible.value = true
    } else if (paper.value?.fileUrl) {
        downloadFile(paper.value.fileUrl)
    }
}

const handleDownloadSuccess = ({ downloadToken }) => {
    if (paper.value?.id) {
        let url = `/api/papers/${paper.value.id}/download`
        const token = getToken()
        if (downloadToken) {
            url += `?downloadToken=${encodeURIComponent(downloadToken)}`
        } else if (token) {
            url += `?token=${encodeURIComponent(token)}`
        }
        downloadFile(url)
        paper.value.downloadCount = (paper.value.downloadCount || 0) + 1
    }
}

const downloadPpt = async () => {
    if (!paper.value?.paperQuestions?.length) return
    exportLoading.value = true
    try {
        const questionsData = paper.value.paperQuestions
            .sort((a, b) => a.order - b.order)
            .map(pq => ({
                ...pq.question,
                typeName: { 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }[pq.question?.type] || '其他'
            }))

        const data = {
            questions: questionsData,
            title: paper.value.name,
            include_answers: true
        }

        const res = await api.post('/api/downloadPpt', data, { responseType: 'blob' })
        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${paper.value.name}.pptx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        ElMessage.success('PPT 导出成功')
    } catch (e) {
        console.error(e)
        ElMessage.error('导出 PPT 失败')
    } finally {
        exportLoading.value = false
    }
}

const usePaper = () => router.push({ path: '/paper', query: { paperId: paper.value.id } })

const goToPaper = (p) => {
    router.push(`/papers/${p.id}`)
}

const goToList = (list) => {
    router.push(`/question-lists/${list.id}`)
}

const getLayoutClass = (q) => {
    const total = q.options?.reduce((s, o) => s + (o.content?.length || 0), 0) || 0
    if (total < 100) return 'layout-1-line'
    if (total < 180) return 'layout-2-line'
    return 'layout-4-line'
}

onMounted(() => {
    fetchPaperDetail()
    loadBasket()
})

watch(() => route.params.id, () => {
    fetchPaperDetail()
})
</script>

<style scoped>
.paper-detail-page {
    padding: 32px 20px 40px;
    background: #f4f7f9;
    min-height: 100vh;
}

.detail-container {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    gap: 30px;
    align-items: flex-start;
}

.main-content {
    flex: 1;
    min-width: 0;
}

.sidebar {
    width: 320px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    position: sticky;
    top: 32px;
}

.paper-header-card {
    background: white;
    padding: 30px;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
}

.breadcrumb {
    margin-bottom: 20px;
}

.paper-title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 20px;
}

.paper-title {
    font-size: 28px;
    font-weight: 800;
    margin: 0;
    color: #1a1a1a;
    line-height: 1.3;
}

.paper-actions {
    display: flex;
    gap: 12px;
}

.paper-meta-info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
    color: #606266;
    font-size: 14px;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.paper-mode-inline {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed #e8edf4;
}

.mode-inline-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}

.mode-ext {
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(26, 115, 232, 0.08);
    color: #1a73e8;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
}

.mode-inline-desc {
    margin: 0;
    font-size: 14px;
    color: #607080;
    line-height: 1.7;
}

.content-section {
    background: white;
    padding: 30px;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    margin-bottom: 24px;
}

.section-title {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
    color: #1a1a1a;
}

.iframe-wrapper {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #edf2f7;
    background: #f8fbff;
}

.paper-gallery {
    background: #f8fafc;
    padding: 30px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
}

.page-wrapper {
    background: white;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    width: 100%;
    max-width: 900px;
    border-radius: 4px;
    overflow: hidden;
}

.page-num {
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: #909399;
    background: #fff;
    border-top: 1px solid #f0f2f5;
}

.archive-section {
    border: 1px solid #f4e2bc;
    background: linear-gradient(135deg, #fffdf8 0%, #fffaf2 100%);
}

.archive-preview-panel {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 18px;
    align-items: center;
    padding: 18px;
    border-radius: 14px;
    background: #fff;
    border: 1px dashed #e9c787;
}

.archive-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    color: #d88900;
    background: rgba(255, 190, 79, 0.16);
}

.archive-title {
    margin: 0;
    font-size: 18px;
    font-weight: 800;
    color: #5e3d00;
}

.archive-desc {
    margin: 8px 0 12px;
    color: #7b5a21;
    font-size: 14px;
}

.archive-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.archive-actions {
    display: flex;
    align-items: center;
}

.exam-paper-section {
    background: #f7f8fa;
    border: 1px solid #e5e7eb;
    font-family: "SimSun", "Songti SC", "STSong", "NSimSun", serif;
}

.exam-paper-head {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 22px 20px 18px;
    margin-bottom: 16px;
    text-align: center;
}

.exam-paper-title {
    margin: 0 0 10px;
    font-size: 24px;
    font-weight: 700;
    color: #1f1f1f;
    letter-spacing: 1px;
    font-family: "SimSun", "Songti SC", "STSong", "NSimSun", serif;
}

.exam-paper-meta-line {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    color: #6b6b6b;
    font-size: 13px;
}

.exam-paper-meta-line span+span::before {
    content: "｜";
    margin-right: 8px;
    color: #b4b4b4;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.exam-toolbar {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 14px;
}

.exam-section-title {
    margin-bottom: 0;
    font-size: 16px;
    letter-spacing: 0.3px;
}

.q-group {
    margin-bottom: 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px 18px;
}

.g-title {
    font-size: 19px;
    font-weight: 700;
    margin-bottom: 6px;
    color: #202020;
    border-left: none;
    border-bottom: 1px solid #eceff3;
    padding: 0 0 10px;
    letter-spacing: 1px;
    font-family: "SimSun", "Songti SC", "STSong", "NSimSun", serif;
}

.q-item {
    padding: 4px 0;
    padding-right: 120px;
    border-bottom: 1px solid #eef1f5;
    display: block;
    gap: 0;
}

.q-item:last-child {
    border-bottom: none;
}

.q-body {
    flex: 1;
    min-width: 0;
}

.q-text {
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.q-no {
    min-width: 28px;
    text-align: right;
    color: #1d1d1d;
    font-size: 18px;
    line-height: 1.75;
    font-weight: 700;
    font-family: "SimSun", "Songti SC", "STSong", "NSimSun", serif;
}

.q-content {
    flex: 1;
    min-width: 0;
}

.q-opts {
    margin-top: 1px;
    margin-left: 34px;
}

.q-actions {
    position: absolute;
    right: 0;
    top: 4px;
    margin-top: 0;
    display: flex;
    justify-content: flex-end;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-3px);
    transition: all 0.18s ease;
    pointer-events: none;
}

.q-item:hover .q-actions,
.q-item:focus-within .q-actions {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
}

.sidebar-card {
    background: white;
    padding: 24px;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f0f2f5;
}

.sidebar-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.sidebar-item {
    cursor: pointer;
    transition: all 0.2s;
}

.sidebar-item:hover .item-name {
    color: #409eff;
}

.item-name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.item-meta {
    font-size: 12px;
    color: #909399;
    display: flex;
    gap: 10px;
}

.sticky-actions {
    margin-top: 10px;
}

.full-btn {
    width: 100%;
}

.layout-1-line {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
}

.layout-2-line {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}

.layout-4-line {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
}

.opt-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.opt-label {
    font-weight: 700;
    color: #272727;
    margin-top: 1px;
    /* slight visual tweak to align with text */
}

.exam-paper-section :deep(.md-editor-preview) {
    color: #1f1f1f;
    line-height: 1.68 !important;
    font-size: 17px !important;
    font-family: "SimSun", "Songti SC", "STSong", "NSimSun", serif;
}

.exam-paper-section .q-content :deep(.md-editor-preview > *:first-child) {
    margin-top: 0 !important;
}

.exam-paper-section .q-content :deep(.md-editor-preview > *:last-child) {
    margin-bottom: 0 !important;
}

.exam-paper-section .q-content :deep(.md-editor-preview p) {
    margin: 0.08em 0 !important;
}

.exam-paper-section .opt-item :deep(.md-editor-preview) {
    font-size: 16px !important;
}

:deep(.md-editor-preview-wrapper) {
    padding: 0 !important;
    overflow: visible !important;
}

:deep(.md-editor) {
    background: transparent !important;
    border: none !important;
    height: auto !important;
    overflow: visible !important;
}

/* Force md-editor to not have margins on the main paragraph in options */
.opt-item :deep(.md-editor-preview p) {
    margin: 0 !important;
    line-height: inherit;
}

:deep(.md-editor-preview) {
    word-break: break-word;
    overflow: visible !important;
}

:deep(.md-editor-preview img) {
    max-width: 100%;
    max-height: 280px;
    object-fit: contain;
    vertical-align: middle;
}

:deep(.md-editor-preview table) {
    display: block;
    width: 100%;
    overflow-x: auto;
    word-break: keep-all;
}

/* ── Paywall Styles ─────────────────────────── */
.q-item {
    position: relative;
}

.q-item--locked {
    overflow: hidden;
    border-radius: 8px;
    opacity: 0.7;
}

.q-body--blurred {
    filter: blur(4px);
    pointer-events: none;
    user-select: none;
}

/* Minimal lock watermark on each blurred question */
.lock-watermark {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 16px;
    cursor: pointer;
}

.lock-watermark-icon {
    font-size: 14px;
    color: #c0c4cc;
    opacity: 0.8;
}

/* Single paywall wall – shown once between free and locked content */
.paywall-wall {
    margin: 8px 0 0;
    padding: 0 0 40px;
    position: relative;
}

/* Gradient fade-out from the bottom of the last free question */
.paywall-wall::before {
    content: '';
    position: absolute;
    top: -60px;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    pointer-events: none;
    z-index: 1;
}

.paywall-wall-inner {
    background: #fffdf2;
    border: 1px solid #fdeea7;
    border-radius: 24px;
    padding: 48px 40px;
    text-align: center;
    position: relative;
    z-index: 2;
}

.paywall-wall-icon {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #ff9d2f 0%, #ff6b21 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: white;
    margin: 0 auto 24px;
    box-shadow: 0 8px 20px rgba(255, 107, 33, 0.3);
}

.paywall-wall-title {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin: 0 0 12px;
}

.paywall-wall-desc {
    font-size: 15px;
    color: #888;
    margin: 0 0 32px;
}

.paywall-wall-price {
    margin: 0 0 32px;
    color: #ff8a00;
    display: flex;
    align-items: baseline;
    justify-content: center;
}

.price-unit {
    font-size: 20px;
    font-weight: 600;
    margin-right: 2px;
}

.price-val {
    font-size: 48px;
    font-weight: 800;
}

.paywall-premium-btn {
    background: linear-gradient(135deg, #ff9d2f 0%, #ff6b21 100%);
    border: none;
    padding: 14px 48px;
    border-radius: 50px;
    font-size: 18px;
    font-weight: 600;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    box-shadow: 0 10px 25px rgba(255, 107, 33, 0.25);
    transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.paywall-premium-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(255, 107, 33, 0.35);
}

.btn-lock {
    font-size: 20px;
    color: white;
}

/* ── Paper Gallery Preview Lock ──────────────── */
.paper-gallery {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 16px;
}

.page-wrapper {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    background: #fff;
    transition: transform 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.page-img {
    width: 100%;
    display: block;
}

.page-img :deep(.el-image__wrapper) {
    display: block;
}

.page-img :deep(.el-image__inner) {
    width: 100% !important;
    height: auto !important;
    display: block;
    object-fit: contain !important;
}

.page-wrapper--locked .page-img {
    filter: blur(12px) brightness(0.9);
    pointer-events: none;
    user-select: none;
}

.page-lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.05);
    z-index: 10;
    cursor: pointer;
    transition: background 0.3s;
}

.page-lock-overlay:hover {
    background: rgba(0, 0, 0, 0.1);
}

.lock-icon {
    font-size: 64px;
    color: #ff9d2f;
    margin-bottom: 24px;
    filter: drop-shadow(0 4px 12px rgba(255, 107, 33, 0.4));
}

.lock-watermark-icon {
    font-size: 16px;
    color: #ff9d2f;
    opacity: 0.9;
}

.lock-text {
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, rgba(255, 157, 47, 0.9) 0%, rgba(255, 107, 33, 0.9) 100%);
    padding: 12px 28px;
    border-radius: 40px;
    backdrop-filter: blur(4px);
    box-shadow: 0 8px 20px rgba(255, 107, 33, 0.3);
}

.page-num {
    padding: 12px;
    text-align: center;
    font-size: 13px;
    color: #909399;
    background: #fafafa;
    border-top: 1px solid #f0f2f5;
}

@media (max-width: 1200px) {
    .detail-container {
        flex-direction: column;
    }

    .sidebar {
        width: 100%;
        position: static;
    }
}

@media (max-width: 768px) {
    .paper-detail-page {
        padding: 18px 12px 28px;
    }

    .paper-header-card,
    .content-section,
    .sidebar-card {
        padding: 18px;
        border-radius: 12px;
    }

    .paper-title-row {
        flex-direction: column;
        align-items: stretch;
    }

    .paper-title {
        font-size: 22px;
    }

    .paper-actions {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
    }

    .archive-preview-panel {
        grid-template-columns: 1fr;
        text-align: left;
    }

    .archive-icon {
        width: 56px;
        height: 56px;
        font-size: 30px;
    }

    .archive-actions {
        justify-content: flex-start;
    }

    .layout-1-line,
    .layout-2-line {
        grid-template-columns: 1fr;
    }

    .exam-paper-head {
        padding: 16px 14px 14px;
    }

    .exam-paper-title {
        font-size: 20px;
    }

    .exam-paper-meta-line {
        justify-content: flex-start;
        gap: 4px 8px;
    }

    .q-group {
        padding: 10px 12px;
    }

    .g-title {
        font-size: 16px;
    }

    .q-no {
        min-width: 22px;
        font-size: 16px;
        line-height: 1.9;
    }

    .q-opts {
        margin-left: 0;
    }

    .q-item {
        padding-right: 0;
    }

    .q-actions {
        position: static;
        opacity: 1;
        visibility: visible;
        transform: none;
        pointer-events: auto;
        justify-content: flex-start;
        margin-top: 2px;
    }

    .exam-paper-section :deep(.md-editor-preview) {
        font-size: 15px !important;
        line-height: 1.8 !important;
    }

    .exam-paper-section .opt-item :deep(.md-editor-preview) {
        font-size: 14px !important;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
}
</style>
